/**
 * 粒子效果預設庫
 * 提供多種視覺特效預設配置：雪花、火焰、閃電、黑洞等
 */

export type ParticlePresetType = 'snowflake' | 'fire' | 'lightning' | 'blackhole' | 'explosion' | 'magic' | 'storm' | 'supernova' | 'cosmic_burst' | 'green_lotus' | 'unlimited_blade_works' | 'divine_thunder' | 'nine_finale';

export interface ParticlePresetConfig {
  type: ParticlePresetType;
  colors: string[];
  behavior: ParticleBehavior;
  physics: ParticlePhysics;
  visual: ParticleVisual;
  animation: ParticleAnimation;
  spawning: ParticleSpawning;
}

export interface ParticleBehavior {
  movement: 'float' | 'rise' | 'fall' | 'spiral' | 'chain' | 'implode' | 'explode';
  gravity: number;
  drag: number;
  turbulence: number;
}

export interface ParticlePhysics {
  velocity: { min: number; max: number };
  acceleration: { x: number; y: number };
  mass: number;
  elasticity: number;
}

export interface ParticleVisual {
  size: { min: number; max: number };
  opacity: { start: number; end: number };
  scale: { start: number; end: number };
  rotation: { enabled: boolean; speed: number };
  glow: { enabled: boolean; intensity: number };
}

export interface ParticleAnimation {
  lifespan: { min: number; max: number };
  fadeIn: number;
  fadeOut: number;
  pulsing: { enabled: boolean; frequency: number };
}

export interface ParticleSpawning {
  count: { min: number; max: number };
  delay: { min: number; max: number };
  spread: { angle: number; radius: number };
  burst: boolean;
}

/**
 * 預設效果配置
 */
export const PARTICLE_PRESETS: Record<ParticlePresetType, ParticlePresetConfig> = {
  /**
   * ❄️ 雪花效果 - 優雅飄落 (C級基礎效果)
   */
  snowflake: {
    type: 'snowflake',
    colors: ['#ffffff', '#e6f3ff', '#cce7ff', '#b3d9ff', '#f0f8ff'],
    behavior: {
      movement: 'float',
      gravity: 30,
      drag: 0.95,
      turbulence: 0.3,
    },
    physics: {
      velocity: { min: 20, max: 60 },
      acceleration: { x: 0, y: 30 },
      mass: 0.5,
      elasticity: 0.1,
    },
    visual: {
      size: { min: 1, max: 3 },  // C級基礎尺寸
      opacity: { start: 0.6, end: 0.1 },
      scale: { start: 0.8, end: 0.2 },
      rotation: { enabled: true, speed: 0.02 },
      glow: { enabled: false, intensity: 0 },
    },
    animation: {
      lifespan: { min: 800, max: 1200 },  // 縮短生命週期
      fadeIn: 100,
      fadeOut: 400,
      pulsing: { enabled: false, frequency: 0 },
    },
    spawning: {
      count: { min: 30, max: 60 },  // C級: 30-60
      delay: { min: 0, max: 50 },
      spread: { angle: Math.PI * 0.2, radius: 40 },
      burst: false,
    },
  },

  /**
   * 🔥 火焰效果 - 熾熱燃燒 (S級效果)
   */
  fire: {
    type: 'fire',
    colors: ['#dda0dd', '#e6b8ea', '#d8bfd8', '#9932cc', '#ba55d3'],
    behavior: {
      movement: 'rise',
      gravity: -100,
      drag: 0.92,
      turbulence: 0.8,
    },
    physics: {
      velocity: { min: 80, max: 200 },
      acceleration: { x: 0, y: -150 },
      mass: 0.3,
      elasticity: 0,
    },
    visual: {
      size: { min: 5, max: 12 },  // S級特大型尺寸
      opacity: { start: 0.9, end: 0.0 },
      scale: { start: 0.5, end: 1.6 },
      rotation: { enabled: false, speed: 0 },
      glow: { enabled: true, intensity: 1.2 },
    },
    animation: {
      lifespan: { min: 800, max: 1500 },
      fadeIn: 50,
      fadeOut: 600,
      pulsing: { enabled: true, frequency: 0.1 },
    },
    spawning: {
      count: { min: 140, max: 170 },  // S級: 140-170
      delay: { min: 0, max: 150 },
      spread: { angle: Math.PI * 0.4, radius: 40 },
      burst: true,
    },
  },

  /**
   * ⚡ 閃電效果 - 電光鏈鎖 (SS級效果)
   */
  lightning: {
    type: 'lightning',
    colors: ['#ffff00', '#ffffff', '#00ffff', '#87ceeb', '#e0ffff'],
    behavior: {
      movement: 'chain',
      gravity: 0,
      drag: 0.99,
      turbulence: 1.5,
    },
    physics: {
      velocity: { min: 300, max: 600 },
      acceleration: { x: 0, y: 0 },
      mass: 0.1,
      elasticity: 0,
    },
    visual: {
      size: { min: 8, max: 16 },  // SS級超大型尺寸
      opacity: { start: 1.0, end: 0.0 },
      scale: { start: 1.0, end: 1.0 },
      rotation: { enabled: false, speed: 0 },
      glow: { enabled: true, intensity: 2.0 },
    },
    animation: {
      lifespan: { min: 100, max: 300 },
      fadeIn: 0,
      fadeOut: 150,
      pulsing: { enabled: true, frequency: 0.5 },
    },
    spawning: {
      count: { min: 180, max: 210 },  // SS級: 180-210
      delay: { min: 0, max: 50 },
      spread: { angle: Math.PI * 2, radius: 0 },
      burst: true,
    },
  },

  /**
   * 🌀 黑洞效果 - 螺旋吞噬 (SSS級傳說效果)
   */
  blackhole: {
    type: 'blackhole',
    colors: ['#000000', '#4b0082', '#9400d3', '#8b00ff', '#1a1a1a'],
    behavior: {
      movement: 'implode',
      gravity: 800,
      drag: 1.02, // 反向阻力，加速內吸
      turbulence: 0.1,
    },
    physics: {
      velocity: { min: 100, max: 300 },
      acceleration: { x: 0, y: 0 },
      mass: 1.0,
      elasticity: 0,
    },
    visual: {
      size: { min: 12, max: 20 },  // SSS級傳說尺寸
      opacity: { start: 0.8, end: 1.0 },
      scale: { start: 1.5, end: 0.1 },
      rotation: { enabled: true, speed: 0.15 },
      glow: { enabled: true, intensity: 2.5 },
    },
    animation: {
      lifespan: { min: 1500, max: 2500 },
      fadeIn: 100,
      fadeOut: 500,
      pulsing: { enabled: false, frequency: 0 },
    },
    spawning: {
      count: { min: 220, max: 300 },  // SSS級: 220-300
      delay: { min: 0, max: 300 },
      spread: { angle: Math.PI * 2, radius: 120 },
      burst: false,
    },
  },

  /**
   * 💥 爆炸效果 - 劇烈衝擊 (A級效果)
   */
  explosion: {
    type: 'explosion',
    colors: ['#87ceeb', '#add8e6', '#b0e0e6', '#87cefa', '#00bfff'],
    behavior: {
      movement: 'explode',
      gravity: 50,
      drag: 0.88,
      turbulence: 0.6,
    },
    physics: {
      velocity: { min: 150, max: 300 },  // 降低速度
      acceleration: { x: 0, y: 100 },
      mass: 0.8,
      elasticity: 0.3,
    },
    visual: {
      size: { min: 3, max: 8 },  // A級大型尺寸
      opacity: { start: 0.9, end: 0.0 },
      scale: { start: 0.3, end: 1.2 },
      rotation: { enabled: true, speed: 0.1 },
      glow: { enabled: true, intensity: 1.0 },
    },
    animation: {
      lifespan: { min: 500, max: 1000 },  // 縮短生命週期
      fadeIn: 20,
      fadeOut: 300,
      pulsing: { enabled: false, frequency: 0 },
    },
    spawning: {
      count: { min: 110, max: 130 },  // A級: 110-130
      delay: { min: 0, max: 30 },
      spread: { angle: Math.PI * 2, radius: 0 },
      burst: true,
    },
  },

  /**
   * ✨ 魔法效果 - 神秘光芒 (B級效果)
   */
  magic: {
    type: 'magic',
    colors: ['#90ee90', '#98fb98', '#b4ffb4', '#7fff00', '#adff2f'],
    behavior: {
      movement: 'float',
      gravity: -20,
      drag: 0.98,
      turbulence: 0.4,
    },
    physics: {
      velocity: { min: 30, max: 120 },
      acceleration: { x: 0, y: -50 },
      mass: 0.2,
      elasticity: 0.8,
    },
    visual: {
      size: { min: 2, max: 5 },  // B級中型尺寸
      opacity: { start: 0.7, end: 0.1 },
      scale: { start: 0.6, end: 1.0 },
      rotation: { enabled: true, speed: 0.05 },
      glow: { enabled: true, intensity: 0.6 },
    },
    animation: {
      lifespan: { min: 1000, max: 1500 },  // 縮短生命週期
      fadeIn: 200,
      fadeOut: 400,
      pulsing: { enabled: true, frequency: 0.08 },
    },
    spawning: {
      count: { min: 70, max: 100 },  // B級: 70-100
      delay: { min: 0, max: 100 },
      spread: { angle: Math.PI * 0.4, radius: 50 },
      burst: false,
    },
  },

  /**
   * 🌪️ 風暴效果 - 狂亂旋渦
   */
  storm: {
    type: 'storm',
    colors: ['#708090', '#778899', '#b0c4de', '#87ceeb', '#4682b4'],
    behavior: {
      movement: 'spiral',
      gravity: 20,
      drag: 0.96,
      turbulence: 1.2,
    },
    physics: {
      velocity: { min: 150, max: 350 },
      acceleration: { x: 0, y: 50 },
      mass: 0.6,
      elasticity: 0.2,
    },
    visual: {
      size: { min: 2, max: 8 },
      opacity: { start: 0.7, end: 0.2 },
      scale: { start: 1.0, end: 0.5 },
      rotation: { enabled: true, speed: 0.12 },
      glow: { enabled: false, intensity: 0 },
    },
    animation: {
      lifespan: { min: 1200, max: 2000 },
      fadeIn: 150,
      fadeOut: 500,
      pulsing: { enabled: false, frequency: 0 },
    },
    spawning: {
      count: { min: 60, max: 120 },
      delay: { min: 0, max: 100 },
      spread: { angle: Math.PI * 2, radius: 100 },
      burst: false,
    },
  },

  /**
   * 🌟 超新星效果 - 橘色爆發 (SS級效果)
   */
  supernova: {
    type: 'supernova',
    colors: ['#ff8c00', '#ffa500', '#ff7f00', '#ff6347', '#ff4500'],
    behavior: {
      movement: 'explode',
      gravity: -50,
      drag: 0.85,
      turbulence: 1.8,
    },
    physics: {
      velocity: { min: 200, max: 400 },
      acceleration: { x: 0, y: -100 },
      mass: 0.4,
      elasticity: 0.1,
    },
    visual: {
      size: { min: 4, max: 9 },  // 減少至接近A級大小
      opacity: { start: 1.0, end: 0.0 },
      scale: { start: 0.4, end: 1.3 },  // 減少結束縮放
      rotation: { enabled: true, speed: 0.2 },
      glow: { enabled: true, intensity: 2.2 },
    },
    animation: {
      lifespan: { min: 1000, max: 1800 },
      fadeIn: 50,
      fadeOut: 800,
      pulsing: { enabled: true, frequency: 0.15 },
    },
    spawning: {
      count: { min: 120, max: 150 },  // 減少至接近A級數量
      delay: { min: 0, max: 80 },
      spread: { angle: Math.PI * 2, radius: 0 },
      burst: true,
    },
  },

  /**
   * 🌌 宇宙爆發效果 - 金色光輝 (SSS級傳說效果)
   */
  cosmic_burst: {
    type: 'cosmic_burst',
    colors: ['#ffd700', '#ffef94', '#fff68f', '#ffffe0', '#fffacd'],
    behavior: {
      movement: 'explode',
      gravity: -80,
      drag: 0.88,
      turbulence: 2.5,
    },
    physics: {
      velocity: { min: 300, max: 600 },
      acceleration: { x: 0, y: -200 },
      mass: 0.2,
      elasticity: 0,
    },
    visual: {
      size: { min: 5, max: 10 },  // 減少至略大於A級
      opacity: { start: 1.0, end: 0.0 },
      scale: { start: 0.2, end: 1.4 },  // 大幅減少結束縮放
      rotation: { enabled: true, speed: 0.25 },
      glow: { enabled: true, intensity: 3.0 },
    },
    animation: {
      lifespan: { min: 1500, max: 2800 },
      fadeIn: 100,
      fadeOut: 1000,
      pulsing: { enabled: true, frequency: 0.2 },
    },
    spawning: {
      count: { min: 130, max: 160 },  // 大幅減少至接近A級
      delay: { min: 0, max: 200 },
      spread: { angle: Math.PI * 2, radius: 30 },  // 大幅減少擴散範圍
      burst: true,
    },
  },

  /**
   * 🌸 青蓮劍歌效果 - 劍氣指向 (B級效果)
   */
  green_lotus: {
    type: 'green_lotus',
    colors: ['#b4ffb4', '#90ee90', '#98fb98', '#adff2f', '#c8ffc8'],
    behavior: {
      movement: 'float',
      gravity: -10,
      drag: 0.96,
      turbulence: 0.2,
    },
    physics: {
      velocity: { min: 40, max: 80 },
      acceleration: { x: 0, y: -30 },
      mass: 0.3,
      elasticity: 0.5,
    },
    visual: {
      size: { min: 2, max: 4 },  // B級中小型尺寸
      opacity: { start: 0.8, end: 0.1 },
      scale: { start: 0.6, end: 1.2 },
      rotation: { enabled: true, speed: 0.03 },
      glow: { enabled: true, intensity: 0.8 },
    },
    animation: {
      lifespan: { min: 1000, max: 1500 },
      fadeIn: 200,
      fadeOut: 500,
      pulsing: { enabled: true, frequency: 0.05 },
    },
    spawning: {
      count: { min: 60, max: 90 },  // B級: 60-90
      delay: { min: 0, max: 80 },
      spread: { angle: Math.PI * 0.3, radius: 40 },
      burst: false,
    },
  },

  /**
   * ⚔️ 無限劍制效果 - 劍雨落下 (C級效果)
   */
  unlimited_blade_works: {
    type: 'unlimited_blade_works',
    colors: ['#c0c0c0', '#d3d3d3', '#dcdcdc', '#e0e0e0', '#f0f0f0'],
    behavior: {
      movement: 'fall',
      gravity: 800,
      drag: 0.99,
      turbulence: 0.1,
    },
    physics: {
      velocity: { min: 300, max: 500 },
      acceleration: { x: 0, y: 800 },
      mass: 0.8,
      elasticity: 0,
    },
    visual: {
      size: { min: 1, max: 2 },  // C級小型尺寸
      opacity: { start: 0.7, end: 0.2 },
      scale: { start: 0.5, end: 0.7 },
      rotation: { enabled: false, speed: 0 },
      glow: { enabled: true, intensity: 0.4 },
    },
    animation: {
      lifespan: { min: 800, max: 1200 },
      fadeIn: 150,
      fadeOut: 200,
      pulsing: { enabled: false, frequency: 0 },
    },
    spawning: {
      count: { min: 20, max: 40 },  // C級: 20-40
      delay: { min: 0, max: 60 },
      spread: { angle: Math.PI * 0.2, radius: 30 },
      burst: false,
    },
  },

  /**
   * ⚡ 神劍御雷效果 - 分形閃電 (S級效果)
   */
  divine_thunder: {
    type: 'divine_thunder',
    colors: ['#c8a0ff', '#b480ff', '#dda0dd', '#9932cc', '#ba55d3'],
    behavior: {
      movement: 'chain',
      gravity: 0,
      drag: 0.98,
      turbulence: 2.0,
    },
    physics: {
      velocity: { min: 400, max: 800 },
      acceleration: { x: 0, y: 0 },
      mass: 0.1,
      elasticity: 0,
    },
    visual: {
      size: { min: 6, max: 12 },  // S級大型尺寸
      opacity: { start: 1.0, end: 0.0 },
      scale: { start: 1.0, end: 1.2 },
      rotation: { enabled: false, speed: 0 },
      glow: { enabled: true, intensity: 2.5 },
    },
    animation: {
      lifespan: { min: 1500, max: 2000 },
      fadeIn: 120,
      fadeOut: 250,
      pulsing: { enabled: true, frequency: 0.3 },
    },
    spawning: {
      count: { min: 120, max: 160 },  // S級: 120-160
      delay: { min: 0, max: 100 },
      spread: { angle: Math.PI * 2, radius: 80 },
      burst: true,
    },
  },

  /**
   * 🔥 九俱焚滅效果 - 流星雨集結 (SS級效果)
   */
  nine_finale: {
    type: 'nine_finale',
    colors: ['#ff8c40', '#ff5028', '#ffb870', '#ff6030', '#ffa060'],
    behavior: {
      movement: 'explode',
      gravity: 0.8,
      drag: 0.95,
      turbulence: 1.5,
    },
    physics: {
      velocity: { min: 600, max: 1000 },
      acceleration: { x: 0, y: 500 },
      mass: 0.3,
      elasticity: 0.1,
    },
    visual: {
      size: { min: 8, max: 16 },  // SS級超大型尺寸
      opacity: { start: 1.0, end: 0.0 },
      scale: { start: 0.8, end: 1.4 },
      rotation: { enabled: true, speed: 2.0 },
      glow: { enabled: true, intensity: 3.0 },
    },
    animation: {
      lifespan: { min: 1600, max: 2200 },
      fadeIn: 100,
      fadeOut: 300,
      pulsing: { enabled: true, frequency: 0.5 },
    },
    spawning: {
      count: { min: 180, max: 210 },  // SS級: 180-210
      delay: { min: 0, max: 120 },
      spread: { angle: Math.PI * 2, radius: 100 },
      burst: true,
    },
  },
};

/**
 * 根據遊戲等級獲取建議的粒子效果
 */
export function getPresetForGrade(grade: string): ParticlePresetType {
  switch (grade) {
    case 'SSS':
      return 'cosmic_burst';
    case 'SS':
      return 'nine_finale';
    case 'S':
      return 'divine_thunder';
    case 'A':
      return 'explosion';
    case 'B':
      return 'green_lotus';
    case 'C':
      return 'unlimited_blade_works';
    default:
      return 'snowflake';
  }
}

/**
 * 根據特殊情況獲取粒子效果
 */
export function getPresetForSpecialMove(type: 'corner' | 'massive' | 'comeback' | 'domination' | 'combo'): ParticlePresetType {
  switch (type) {
    case 'corner':
      return 'explosion';
    case 'massive':
      return 'storm';
    case 'comeback':
      return 'fire';
    case 'domination':
      return 'blackhole';
    case 'combo':
      return 'lightning';
    default:
      return 'magic';
  }
}

/**
 * 混合兩種預設效果
 */
export function blendPresets(preset1: ParticlePresetType, preset2: ParticlePresetType, ratio: number = 0.5): ParticlePresetConfig {
  const config1 = PARTICLE_PRESETS[preset1];
  const config2 = PARTICLE_PRESETS[preset2];

  return {
    type: preset1,
    colors: [...config1.colors, ...config2.colors],
    behavior: {
      movement: ratio > 0.5 ? config1.behavior.movement : config2.behavior.movement,
      gravity: config1.behavior.gravity * ratio + config2.behavior.gravity * (1 - ratio),
      drag: config1.behavior.drag * ratio + config2.behavior.drag * (1 - ratio),
      turbulence: config1.behavior.turbulence * ratio + config2.behavior.turbulence * (1 - ratio),
    },
    physics: {
      velocity: {
        min: Math.min(config1.physics.velocity.min, config2.physics.velocity.min),
        max: Math.max(config1.physics.velocity.max, config2.physics.velocity.max),
      },
      acceleration: {
        x: config1.physics.acceleration.x * ratio + config2.physics.acceleration.x * (1 - ratio),
        y: config1.physics.acceleration.y * ratio + config2.physics.acceleration.y * (1 - ratio),
      },
      mass: config1.physics.mass * ratio + config2.physics.mass * (1 - ratio),
      elasticity: config1.physics.elasticity * ratio + config2.physics.elasticity * (1 - ratio),
    },
    visual: {
      size: {
        min: Math.min(config1.visual.size.min, config2.visual.size.min),
        max: Math.max(config1.visual.size.max, config2.visual.size.max),
      },
      opacity: {
        start: Math.max(config1.visual.opacity.start, config2.visual.opacity.start),
        end: Math.min(config1.visual.opacity.end, config2.visual.opacity.end),
      },
      scale: {
        start: config1.visual.scale.start * ratio + config2.visual.scale.start * (1 - ratio),
        end: config1.visual.scale.end * ratio + config2.visual.scale.end * (1 - ratio),
      },
      rotation: {
        enabled: config1.visual.rotation.enabled || config2.visual.rotation.enabled,
        speed: config1.visual.rotation.speed * ratio + config2.visual.rotation.speed * (1 - ratio),
      },
      glow: {
        enabled: config1.visual.glow.enabled || config2.visual.glow.enabled,
        intensity: Math.max(config1.visual.glow.intensity, config2.visual.glow.intensity),
      },
    },
    animation: {
      lifespan: {
        min: Math.min(config1.animation.lifespan.min, config2.animation.lifespan.min),
        max: Math.max(config1.animation.lifespan.max, config2.animation.lifespan.max),
      },
      fadeIn: Math.max(config1.animation.fadeIn, config2.animation.fadeIn),
      fadeOut: Math.max(config1.animation.fadeOut, config2.animation.fadeOut),
      pulsing: {
        enabled: config1.animation.pulsing.enabled || config2.animation.pulsing.enabled,
        frequency: config1.animation.pulsing.frequency * ratio + config2.animation.pulsing.frequency * (1 - ratio),
      },
    },
    spawning: {
      count: {
        min: Math.max(config1.spawning.count.min, config2.spawning.count.min),
        max: Math.max(config1.spawning.count.max, config2.spawning.count.max),
      },
      delay: {
        min: Math.min(config1.spawning.delay.min, config2.spawning.delay.min),
        max: Math.max(config1.spawning.delay.max, config2.spawning.delay.max),
      },
      spread: {
        angle: Math.max(config1.spawning.spread.angle, config2.spawning.spread.angle),
        radius: Math.max(config1.spawning.spread.radius, config2.spawning.spread.radius),
      },
      burst: config1.spawning.burst || config2.spawning.burst,
    },
  };
}